import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import _ from 'underscore';

import {pluralize} from 'BenchlingUtil';
import * as UserDAO from 'dao/UserDAO';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {optimisticallyUpdate} from 'lib/OptimisticallyUpdate';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import {UserIsSuspended} from 'tenants/admin/__generated/UserIsSuspended';
import {UserUpdateProfile} from 'tenants/admin/__generated/UserUpdateProfile';
import {SuspendUserConfirmationModal} from 'tenants/admin/SuspendUserConfirmationModal';
import {UserSettingsModalUser} from 'tenants/admin/UserSettingsModal';
import legacyRunModal from 'ui-library/legacyRunModal';
import pickDefined from 'util/pickDefined';

const USER_IS_SUSPENDED_FRAGMENT: DocumentNode = gql`
  fragment UserIsSuspended on UserNode {
    id
    suspended
  }
`;

const UserIsSuspendedFragment: FragmentWrapper<UserIsSuspended> = {
  fragment: USER_IS_SUSPENDED_FRAGMENT,
  fragmentName: 'UserIsSuspended',
};

export function updateUserIsSuspended(isSuspendedByUserId: {[userId: string]: boolean}): void {
  Object.entries(isSuspendedByUserId).forEach(([userId, isSuspended]) => {
    writeFragmentsToCache(UserIsSuspendedFragment, [
      {
        __typename: 'UserNode',
        id: userId,
        suspended: isSuspended,
      },
    ]);
  });
}

function makeIsSuspendedByUserId(userIds: Array<string>, isSuspended: boolean): {[userId: string]: boolean} {
  // eslint-disable-next-line benchlint/no-spread-reduce
  return userIds.reduce(
    (userIdToIsSuspended: {[userId: string]: boolean}, userId: string) => ({
      ...userIdToIsSuspended,
      [userId]: isSuspended,
    }),
    {}
  );
}

export function verifySuspendedUsers(
  userIds: Array<string>,
  isSuspended: boolean,
  onComplete?: () => void
): void {
  const currentUser = EntityStore.currentUser();
  let selfSuspendText;
  if (isSuspended && userIds.includes(currentUser.id)) {
    // eslint-disable-next-line no-restricted-syntax
    userIds = _.without(userIds, currentUser.id);
    selfSuspendText = 'Your own user will not be suspended';
    if (!userIds.length) {
      notifications.warn('You cannot suspend yourself.');
      return;
    }
  }

  if (isSuspended) {
    legacyRunModal(SuspendUserConfirmationModal, {
      suspendMultiple: userIds.length > 1,
      onSuspend: () => updateSuspendedUsers(userIds, isSuspended, onComplete),
      warningText: selfSuspendText || undefined,
    });
  } else {
    updateSuspendedUsers(userIds, isSuspended, onComplete);
  }
}

async function updateSuspendedUsers(
  userIds: Array<string>,
  isSuspended: boolean,
  onComplete?: () => void
): Promise<void> {
  const isSuspendedByUserId = makeIsSuspendedByUserId(userIds, isSuspended);
  const revertIsSuspendedByUserId = makeIsSuspendedByUserId(userIds, !isSuspended);

  const setUsersSuspended = async (): Promise<void> => {
    try {
      await UserDAO.suspendUsers(isSuspendedByUserId);
      const successText = `Successfully ${isSuspended ? 'suspended' : 'unsuspended'} ${pluralize(
        userIds.length,
        'user'
      )}.`;
      notifications.success(successText);
    } catch (e) {
      // If the error is a BadRequestError, display the accompanying error message
      if (e.status !== 400) {
        const errorText = `Could not ${isSuspended ? 'suspend' : 'unsuspend'} ${pluralize(
          userIds.length,
          'user'
        )}.`;
        notifications.error(errorText);
        return;
      }
      throw e;
    }
  };
  await optimisticallyUpdate<{[userId: string]: boolean}, void>({
    persistData: setUsersSuspended,
    writeDataToCache: updateUserIsSuspended,
    optimisticData: isSuspendedByUserId,
    revertData: revertIsSuspendedByUserId,
  });
  onComplete?.();
}

const USER_UPDATE_PROFILE_FRAGMENT: DocumentNode = gql`
  fragment UserUpdateProfile on UserNode {
    id
    name
    handle
    email
  }
`;

const UserUpdateProfileFragment: FragmentWrapper<UserUpdateProfile> = {
  fragment: USER_UPDATE_PROFILE_FRAGMENT,
  fragmentName: 'UserUpdateProfile',
};

export function writeUserProfileToCache(user: UserSettingsModalUser): void {
  writeFragmentsToCache(UserUpdateProfileFragment, [
    {
      __typename: 'UserNode',
      id: user.id,
      name: user.name,
      handle: user.handle,
      email: user.email,
    },
  ]);
}

export async function updateUserProfile(
  initialUser: UserSettingsModalUser,
  user: UserSettingsModalUser
): Promise<void> {
  const updateUserInfo = pickDefined(user, 'name', 'handle', 'email');
  const setUserProfile = async (): Promise<void> => {
    try {
      await UserDAO.updateUser(user.id, updateUserInfo);
      notifications.success('Successfully updated user information.');
    } catch (e) {
      // If the error is a BadRequestError, display the accompanying error message
      if (e.status !== 400) {
        notifications.error('Could not update user information.');
        return;
      }
      throw e;
    }
  };
  await optimisticallyUpdate<UserSettingsModalUser, void>({
    persistData: setUserProfile,
    writeDataToCache: writeUserProfileToCache,
    optimisticData: user,
    revertData: initialUser,
  });
}
